import { IconButton } from "@mui/material"
import { CheckOutlined, CloseOutlined, EditOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"

import { formatStockType } from "../../utils/stockType"

import type { ProductWarehouseArea, WarehouseArea } from "@/graphql/codegen/graphql"
import { LeadTimeUnits } from "@/graphql/codegen/graphql"
import { formatLeadTimeDisplay } from "@/screens/Products/utils"
import { formatNumber } from "@/utils/number"
import { isNil } from "@/utils/utils"
import { formatWarehouseLabel } from "@/utils/warehouse"

export const AKROCHEM_GROUP_ID = "akrochem"

interface UseProductWarehouseColumnsProps {
  isPastProductVersion: boolean
  packageUsUomName?: string | null
  onEdit: (warehouseArea: ProductWarehouseArea) => void
}

interface UseProductWarehouseColumnsReturn {
  columns: ColumnDef<ProductWarehouseArea>[]
}

export const useProductWarehouseColumns = ({
  isPastProductVersion,
  packageUsUomName,
  onEdit,
}: UseProductWarehouseColumnsProps): UseProductWarehouseColumnsReturn => {
  const columns = useMemo<ColumnDef<ProductWarehouseArea>[]>(
    () => [
      {
        accessorKey: "externalSystemId",
        header: "Warehouse",
        cell: ({ row }) => (
          <div className='flex items-center gap-x-2'>
            <span
              className={`inline-block size-2 rounded-full ${row.original.isActive ? "bg-tertiary-green-500" : "bg-red-600"}`}
            />
            {formatWarehouseLabel(row.original as WarehouseArea)}
          </div>
        ),
        enableSorting: true,
      },
      {
        id: AKROCHEM_GROUP_ID,
        header: "Akrochem Details",
        columns: [
          {
            accessorKey: "minimumQuantity",
            header: "Safety Stock (US UoM)",
            cell: ({ row }) =>
              isNil(row.original.minimumQuantity)
                ? ""
                : `${formatNumber(row.original.minimumQuantity)} ${packageUsUomName ?? ""}`,
            enableSorting: true,
          },
          {
            accessorKey: "stockType",
            header: "Stock Status",
            cell: ({ row }) => formatStockType(row.original.stockType),
            enableSorting: true,
          },
          {
            accessorKey: "excludedFromCombinedPricing",
            header: "Combined Pricing",
            cell: ({ row }) =>
              row.original.excludedFromCombinedPricing ? (
                <>
                  <CloseOutlined className='text-red-600' /> Excluded
                </>
              ) : (
                <>
                  <CheckOutlined className='text-tertiary-green-700' /> Included
                </>
              ),
            enableSorting: true,
          },
          {
            accessorKey: "isOnAllocation",
            header: "Allocation",
            cell: ({ row }) =>
              row.original.isOnAllocation ? (
                <>
                  <CheckOutlined className='text-tertiary-green-700' /> On Allocation
                </>
              ) : (
                <>
                  <CloseOutlined className='text-red-600' /> Not On Allocation
                </>
              ),
            enableSorting: true,
          },
        ],
      },
      {
        id: "vendor",
        header: "Vendor Details",
        columns: [
          {
            accessorKey: "weightPerTruckload",
            header: "US UoM per Truckload",
            cell: ({ row }) =>
              isNil(row.original.weightPerTruckload)
                ? ""
                : `${formatNumber(row.original.weightPerTruckload)} ${packageUsUomName ?? ""}`,
            enableSorting: true,
          },
          {
            accessorKey: "leadTimeInDays",
            header: "Lead Time",
            cell: ({ row }) =>
              row.original.leadTimeInDays &&
              formatLeadTimeDisplay(
                row.original.leadTimeInDays,
                row.original.leadTimeDisplayUnits ?? LeadTimeUnits.Days
              ),
            enableSorting: true,
          },
          {
            accessorKey: "minOrderQuantity",
            header: "Min Order Qty (US UoM)",
            cell: ({ row }) =>
              isNil(row.original.minOrderQuantity)
                ? ""
                : `${formatNumber(row.original.minOrderQuantity)} ${packageUsUomName ?? ""}`,
            enableSorting: true,
          },
        ],
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) =>
          isPastProductVersion ? null : (
            <div className='flex items-center justify-end'>
              <IconButton
                color='primary'
                aria-label='edit warehouse details'
                onClick={() => onEdit(row.original)}
                data-testid={`product.warehouse-table.edit-button-${row.original.warehouseAreaId}`}
              >
                <EditOutlined />
              </IconButton>
            </div>
          ),
        enableSorting: false,
        size: 60,
      },
    ],
    [isPastProductVersion, packageUsUomName, onEdit]
  )

  return { columns }
}
